import React from 'react';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { InfoTooltip } from './InfoTooltip';
import { handlingTooltipText, hasSomethingToSay } from './HandlingStripFace';

// ─── CategoryBadge ──────────────────────────────────────
// The badge EXPLAINS ITSELF on hover as of vocabulary v7 (2026-09-13): wim's default row layout
// became strip_label on that date, which makes this badge — not the strip — the face a clerk reads
// all day, and the served `hint` is the sentence saying what the handling means. The bubble is the
// house InfoTooltip carrying handlingTooltipText, the SAME text the strip's own hover shows, so the
// two surfaces on one row cannot word the same fact differently.
// The badge SPAN is untouched (it is the tooltip's anchor, as every InfoTooltip call site passes a
// host element) — nothing about its box, colour or face changed, so no caller's geometry moves.
//
// ONLY WHEN THERE IS SOMETHING TO SAY (final review of v7, same date). InfoTooltip makes its anchor
// keyboard-reachable by giving a non-focusable host tabIndex=0 — correct for a bubble carrying a
// fact, and a tab stop per badge on every list row of gMSB/wMSB/VNB when the bubble only repeats the
// word already printed on the badge. So the wrapper goes on for exactly two reasons: a served hint
// (the sentence saying what the handling MEANS), or a FACE that is not the label (the compact/id
// face, where the bubble is the only place the German word appears). Otherwise the bare span renders
// exactly as it did before v7 — and because InfoTooltip CLONES its host rather than wrapping it in
// an element of its own, the two arms are the same DOM box either way: no metric moves when a
// vocabulary landing adds a hint to a value (UI Layout Stability Rule).
// `tooltip` (Archiv ledger fix, operator ruling B9, 2026-09-16, C3.4: "remove the hover-over from
// the Archivseite") lets a surface rule the bubble OUT entirely, overriding hasSomethingToSay —
// the ledger is a scanning surface, not a deciding one, so its badge never tooltips even for a
// value that would otherwise earn one. Default true, so every call site written before this prop
// existed stays byte-identical.
export const CategoryBadge: React.FC<{ category: string; variant?: 'solid' | 'ghost'; style?: React.CSSProperties; tooltip?: boolean }> = ({ category, variant = 'solid', style, tooltip = true }) => {
  const cm = useCategoryMeta();
  const ghost = variant === 'ghost';
  const face = cm.categoryLabelsFromData ? cm.label(category) : category;
  // part 7 fix loop, 2026-10-12: the unclassified value is the slate tint, not coloured (cm.swatch). The
  // 1.5px border is on every solid badge (its own fill colour) so both forms share one box.
  const sw = cm.swatch(category);
  const badge = (
    <span style={{
      fontSize: 10, fontWeight: 700,
      color: ghost ? theme.colors.navy : sw.ink,
      backgroundColor: ghost ? theme.colors.white : sw.fill,
      ...(ghost ? {} : { border: sw.border, boxSizing: 'border-box' as const }),
      borderRadius: theme.borderRadius.sm, padding: '2px 6px',
      display: 'inline-block',
      ...style,
    }}>
      {face}
    </span>
  );
  if (!tooltip || !hasSomethingToSay(cm, category, face)) return badge;
  return <InfoTooltip text={handlingTooltipText(cm, category)}>{badge}</InfoTooltip>;
};
